import * as React from "react";

import { useAccountSettings, URLS, useSocialCoachSession } from "@/utils";
import { WebComponents } from "@/interfaces";

import VideoCreditUsageModal from "./VideoCreditUsageModal";
import VideoProjectsList from "./VideoProjectsList";
import VideoProjectsSearchList from "./VideoProjectsSearchList";
import { EmptyView } from "@/components/EmptyView";
import { Button, Spinner, Combobox } from "@/components/ui";
import * as DTO from "../../../../interfaces";
import { VideoProjectServices } from "@/services";
import { RouteComponentProps, withRouter } from "react-router-dom";

const VideoProjects: React.FC<RouteComponentProps> = ({ history }) => {
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const writePermission = hasWritePermission(WebComponents.VIDEO_CATALYST);

  const [showCreditUsage, setShowCreditUsage] = React.useState(false);
  const [loadingPlayers, setLoadingPlayers] = React.useState(false);
  const [loadingVideoProjects, setLoadingVideoProjects] = React.useState(false);
  const [searchedVideoProjectsList, setSearchedVideoProjectsList] = React.useState<DTO.BaseVideoProject[]>([]);
  const [players, setPlayers] = React.useState<DTO.PlayerResumeResponse[]>([]);

  type VideoProjectStatusOption = {
    key: number;
    label: string;
    value: string;
  };
  const [selectedStatus, setSelectedStatus] = React.useState<VideoProjectStatusOption>();
  const statuses: VideoProjectStatusOption[] = [
    "SUBMITTED FOR EDITING",
    "READY TO POST",
    "SCHEDULED",
    "POSTED",
    "POST FAILED",
  ].map((status, index) => ({
    key: index,
    label: status,
    value: status.replaceAll(" ", "_"),
  }));

  React.useEffect(() => {
    if (coachId) {
      setLoadingPlayers(true);
      VideoProjectServices.getPlayersWithVideoProjects(coachId)
        .then(result => {
          setPlayers(result);
        })
        .finally(() => {
          setLoadingPlayers(false);
        });
    }
  }, [coachId]);

  React.useEffect(() => {
    if (!selectedStatus || !coachId) {
      return;
    }

    setLoadingVideoProjects(true);
    const status = [selectedStatus.value];

    VideoProjectServices.search(coachId, "createdDate,DESC", { status }, 0, 50)
      .then(response => {
        setSearchedVideoProjectsList(response.content);
      })
      .finally(() => {
        setLoadingVideoProjects(false);
      });
  }, [selectedStatus]);

  const content = React.useMemo(() => {
    if (loadingVideoProjects || loadingPlayers) {
      return <Spinner className="size-12 self-center mt-25" />;
    } else if (players.length === 0) {
      return <EmptyView text="No Video Projects" />;
    } else if (selectedStatus) {
      const playerVideoProjectComponents = players
        .map(player => {
          const videoProjects = searchedVideoProjectsList.filter(vp => vp.playerId === player.playerId);
          if (videoProjects.length === 0) return undefined;
          return (
            <VideoProjectsSearchList
              key={"player_video_project_section_" + player.playerId}
              player={player}
              videoProjects={videoProjects}
            />
          );
        })
        .filter(component => component !== undefined);
      if (playerVideoProjectComponents.length === 0) {
        return <EmptyView text="No video projects found for the selected status" />;
      } else {
        return playerVideoProjectComponents;
      }
    } else {
      return players.map(player => (
        <VideoProjectsList key={"video_catalyst_list_c_" + player.playerId} coachId={coachId} player={player} />
      ));
    }
  }, [searchedVideoProjectsList, players, selectedStatus, loadingVideoProjects, loadingPlayers, coachId]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 items-center flex-wrap justify-between">
        <div className="flex gap-4 items-center flex-wrap flex-auto">
          <Combobox
            className="max-w-80 min-w-50"
            items={statuses}
            value={selectedStatus}
            placeholder="Filter By Status"
            showClear
            onValueChange={value => setSelectedStatus(value || undefined)}
          />
        </div>
        {writePermission && (
          <div className="flex gap-4 items-center flex-wrap">
            <Button variant="outline" onClick={() => setShowCreditUsage(true)} children="Credit Usage Report" />
            <Button onClick={() => history.push(URLS.coach.newVideoProject)} children="New Project" />
          </div>
        )}
      </div>
      {content}
      <VideoCreditUsageModal open={showCreditUsage} closeHandler={() => setShowCreditUsage(false)} coachId={coachId} />
    </div>
  );
};

export default withRouter(VideoProjects);
